import { useRef, useState } from "react";
import { BookOpen, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function TemplateCard({ templates, templateId, setTemplateId, reload }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post("/lesson-plans/templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(r.data.fillable ? `Template ready — ${r.data.section_labels.length} boxes detected.` : "Template saved (used as a style guide).");
      setTemplateId(r.data.id);
      await reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (id) => {
    await api.delete(`/lesson-plans/templates/${id}`);
    if (templateId === id) setTemplateId("");
    reload();
  };

  return (
    <div className="hq-glass rounded-2xl p-4" data-testid="lp-template-card">
      <p className="text-sm font-medium text-slate-200 mb-1 flex items-center gap-2"><BookOpen className="w-4 h-4 text-[#22D3EE]" /> Lesson Plan Template</p>
      <p className="text-xs text-muted-foreground mb-3">Upload your school's one-day .docx template. Every box gets filled — header boxes (teacher, date, class, next assessment) from Plan Information, the rest by AI — and the week download repeats it once per day.</p>
      <input ref={fileRef} type="file" accept=".docx,.pdf" onChange={onUpload} className="hidden" data-testid="lp-template-input" />
      <Button data-testid="lp-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" className="border-[#22D3EE]/40 text-[#22D3EE] hover:bg-[#22D3EE]/10">
        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Upload .docx or .pdf
      </Button>
      <div className="mt-3 space-y-2" data-testid="lp-template-list">
        <label className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer ${!templateId ? "border-[#22D3EE]/60 bg-[#22D3EE]/5" : "border-white/10"}`}>
          <input type="radio" name="lp-tpl" data-testid="lp-template-builtin" checked={!templateId} onChange={() => setTemplateId("")} className="accent-[#22D3EE]" />
          <span className="text-sm text-slate-200">Built-in layout <span className="text-muted-foreground">· no upload needed</span></span>
        </label>
        {templates.map((t) => (
          <label key={t.id} data-testid={`lp-template-${t.id}`} className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 cursor-pointer ${templateId === t.id ? "border-[#22D3EE]/60 bg-[#22D3EE]/5" : "border-white/10"}`}>
            <span className="flex items-center gap-3 min-w-0">
              <input type="radio" name="lp-tpl" checked={templateId === t.id} onChange={() => setTemplateId(t.id)} className="accent-[#22D3EE]" />
              <span className="min-w-0">
                <span className="block text-sm text-slate-200 truncate">{t.name}</span>
                <span className="block text-[11px] text-muted-foreground">{t.format.toUpperCase()} · {t.fillable ? <span className="text-emerald-400">✓ fillable · {t.section_labels?.length} boxes</span> : "style guide only"}</span>
              </span>
            </span>
            <button type="button" data-testid={`lp-template-delete-${t.id}`} onClick={(e) => { e.preventDefault(); remove(t.id); }} className="text-slate-500 hover:text-[#E11D48]"><Trash2 className="w-4 h-4" /></button>
          </label>
        ))}
      </div>
    </div>
  );
}
